/**
 * Customer sign-in: the OTP form, and the viewer it produces.
 *
 * Authority: bead scope-r3x5; `apps/api/src/functions/auth.otp-send.ts`,
 * `auth.otp-verify.ts` (scope-9xn, the API half this wraps).
 *
 * Mirrors `StaffAuthProvider.tsx`'s shape deliberately — engage only on this
 * plane, render something instead of the route until a credential exists —
 * but the two credentials are nothing alike: a customer holds an opaque,
 * `HttpOnly` session cookie the browser already attaches, not a bearer token
 * this file acquires and renews. There is no MSAL, no redirect, and no
 * client-held token anywhere below.
 *
 * **Engages, and writes `ViewerContext`, only on the customer plane.** Off it,
 * this renders `children` completely untouched — no `ViewerContext` wrap at
 * all — so it can never shadow another provider's decision for another plane.
 * `StaffAuthProvider` makes the same promise for the staff plane; the two
 * compose in `main.tsx` because each one stays silent everywhere but its own.
 */
import { useEffect, useReducer, type ReactNode } from 'react'

import { navigate, useLocationPath } from '../app/navigation.ts'
import { DEEP_LINK_PREFIX } from '../person/deepLink.ts'
import { CHOOSER_PATH } from '../person/routes.ts'
import { ViewerContext } from '../app/viewer.ts'
import { ANONYMOUS, type Viewer } from '../routes/_registry/access.ts'
import { Shell } from '../shell/Shell.tsx'
import { CustomerSignInForm } from './CustomerSignInForm.tsx'
import { isCustomerPlanePath, probeCustomerSession } from './customer-session.ts'
import { onEngagementSelected } from '../person/personApi.ts'
import {
  INITIAL_CUSTOMER_SIGN_IN_STATE,
  customerSignInReducer,
  type CustomerSignInAction,
} from './customerSignInState.ts'

const CUSTOMER_VIEWER: Viewer = { kind: 'customer' }

/**
 * Holds a verified-but-unbound viewer on the chooser (IMPL-ENGAGE-002 §8.2).
 *
 * A selection token cannot serve the questionnaire — every read there is
 * isolated by an engagement claim this credential does not carry — so landing
 * on `/questionnaire` in this state produces a screen of failed fetches rather
 * than a sign-in prompt. Redirecting is the honest response: the person has
 * one thing to do, and this is the screen that offers it.
 *
 * `/e/<ref>` is left alone because it does its own forwarding to the chooser,
 * carrying the reference it was given; intercepting it here would drop that.
 */
function ChoosingGate({ children }: { readonly children: ReactNode }) {
  const path = useLocationPath()
  const settled = path === CHOOSER_PATH || path.startsWith(DEEP_LINK_PREFIX)

  useEffect(() => {
    if (!settled) navigate(CHOOSER_PATH)
  }, [settled])

  return settled ? children : null
}

/**
 * Registered once, for the provider's lifetime: the selection can happen on
 * any of the screens below it, and none of them holds this dispatch.
 */
function useSelectionSignal(dispatch: (action: CustomerSignInAction) => void): void {
  useEffect(
    () =>
      onEngagementSelected(() => {
        dispatch({ type: 'ENGAGEMENT_SELECTED' })
      }),
    [dispatch],
  )
}

export function CustomerAuthProvider({ children }: { readonly children: ReactNode }) {
  const onCustomerPlane = isCustomerPlanePath(useLocationPath())
  const [state, dispatch] = useReducer(customerSignInReducer, INITIAL_CUSTOMER_SIGN_IN_STATE)

  useSelectionSignal(dispatch)

  useEffect(() => {
    if (!onCustomerPlane) return
    let cancelled = false
    void probeCustomerSession().then((probe) => {
      if (!cancelled) dispatch({ type: 'SESSION_CHECKED', probe })
    })

    return () => {
      cancelled = true
    }
  }, [onCustomerPlane])

  if (!onCustomerPlane) return children
  if (state.step === 'signed-in') {
    return <ViewerContext value={CUSTOMER_VIEWER}>{children}</ViewerContext>
  }
  /*
   * Verified, but not yet bound to an engagement (§8.2). The viewer is a real
   * customer — the credential is genuine — so the tree renders; `ChoosingGate`
   * is what keeps it on the one screen a selection token can actually serve.
   */
  if (state.step === 'choosing') {
    return (
      <ViewerContext value={CUSTOMER_VIEWER}>
        <ChoosingGate>{children}</ChoosingGate>
      </ViewerContext>
    )
  }

  return (
    <ViewerContext value={ANONYMOUS}>
      <Shell plane="customer">
        <CustomerSignInForm state={state} dispatch={dispatch} />
      </Shell>
    </ViewerContext>
  )
}
